var Collection_1;
import { __decorate } from "tslib";
import { startIO } from '../io-tools.js';
import { define, definitions, EventMap, eventsApi, logger, Mixable, mixinRules, mixins } from '../object-plus/index.js';
import { AggregatedType, Record, SharedType } from '../record/index.js';
import { ItemsBehavior, Transactional, transactionApi } from '../transactions.js';
import { addTransaction } from './add.js';
import { free, sortElements, updateIndex } from './commons.js';
import { removeMany, removeOne } from './remove.js';
import { emptySetTransaction, setTransaction } from './set.js';
const { trigger2 } = eventsApi, { begin, commit, markAsDirty } = transactionApi;
let _count = 0;
class CollectionRefsType extends SharedType {
}
CollectionRefsType.defaultValue = [];
;
let Collection = Collection_1 = class Collection extends Transactional {
    static of(Ctor) {
        return Ctor.Collection;
    }
    static ofRefs(Ctor) {
        return Ctor.Collection.Refs;
    }
    createSubset(models, options) {
        throw new ReferenceError('Failed dependency injection');
    }
    static onExtend(BaseClass) {
        const Ctor = this;
        this._SubsetOf = null;
        function RefsCollection(a, b, listen) {
            Ctor.call(this, a, b, ItemsBehavior.share | (listen ? ItemsBehavior.listen : 0));
        }
        Mixable.mixins.populate(RefsCollection);
        RefsCollection.prototype = this.prototype;
        RefsCollection._metatype = CollectionRefsType;
        this.Refs = this.Subset = RefsCollection;
        Transactional.onExtend.call(this, BaseClass);
    }
    static onDefine(definition, BaseClass) {
        if (definition.itemEvents) {
            const eventsMap = new EventMap(BaseClass.prototype._itemEvents);
            eventsMap.addEventsMap(definition.itemEvents);
            this.prototype._itemEvents = eventsMap;
        }
        if (definition.comparator !== void 0)
            this.prototype.comparator = definition.comparator;
        Transactional.onDefine.call(this, definition);
    }
    get __inner_state__() { return this.models; }
    set comparator(x) {
        switch (typeof x) {
            case 'string':
                this._comparator = (a, b) => {
                    const aa = a[x], bb = b[x];
                    if (aa === bb)
                        return 0;
                    return aa < bb ? -1 : +1;
                };
                break;
            case 'function':
                if (x.length === 1) {
                    this._comparator = (a, b) => {
                        const aa = x.call(this, a), bb = x.call(this, b);
                        if (aa === bb)
                            return 0;
                        return aa < bb ? -1 : +1;
                    };
                }
                else {
                    this._comparator = (a, b) => x.call(this, a, b);
                }
                break;
            default:
                this._comparator = null;
        }
    }
    getStore() {
        return this._store || (this._store = this._owner ? this._owner.getStore() : this._defaultStore);
    }
    get comparator() { return this._comparator; }
    _onChildrenChange(record, options = {}, initiator) {
        if (initiator === this)
            return;
        const { idAttribute } = this;
        if (record.hasChanged(idAttribute)) {
            updateIndex(this._byId, record);
        }
        const isRoot = begin(this);
        if (markAsDirty(this, options)) {
            trigger2(this, 'change', record, options);
        }
        isRoot && commit(this);
    }
    get(objOrId) {
        if (objOrId == null)
            return;
        if (typeof objOrId === 'object') {
            const id = objOrId[this.idAttribute];
            return (id !== void 0 && this._byId[id]) || this._byId[objOrId.cid];
        }
        else {
            return this._byId[objOrId];
        }
    }
    [Symbol.iterator]() {
        return this.models[Symbol.iterator]();
    }
    updateEach(iteratee) {
        const isRoot = transactionApi.begin(this);
        this.each(iteratee);
        isRoot && transactionApi.commit(this);
    }
    _validateNested(errors) {
        if (this._shared)
            return 0;
        let count = 0;
        this.each(record => {
            const error = record.validationError;
            if (error) {
                errors[record.cid] = error;
                count++;
            }
        });
        return count;
    }
    constructor(records, options = {}, shared) {
        super(_count++);
        this.models = [];
        this._byId = {};
        this.comparator = this.comparator;
        if (options.comparator !== void 0) {
            this.comparator = options.comparator;
            options.comparator = void 0;
        }
        this.model = this.model;
        if (options.model) {
            this.model = options.model;
            options.model = void 0;
        }
        this.idAttribute = this.model.prototype.idAttribute;
        this._shared = shared || 0;
        if (records) {
            const elements = toElements(this, records, options);
            emptySetTransaction(this, elements, options, true);
        }
        this.initialize.apply(this, arguments);
        if (this._localEvents)
            this._localEvents.subscribe(this, this);
    }
    initialize() { }
    clone(options = {}) {
        const models = this._shared & ItemsBehavior.share ? this.models : this.map(model => model.clone()), copy = new this.constructor(models, { model: this.model, comparator: this.comparator }, this._shared);
        if (options.pinStore)
            copy._defaultStore = this.getStore();
        return copy;
    }
    toJSON(options) {
        return this.map(model => model.toJSON(options));
    }
    set(elements = [], options = {}) {
        if (options.add !== void 0) {
            this._log('warn', "Type-R:InvalidOption", "Collection.set doesn't support 'add' option, behaving as if options.add === true.", options);
        }
        if (options.reset) {
            this.reset(elements, options);
        }
        else {
            const transaction = this._createTransaction(elements, options);
            transaction && transaction.commit();
        }
        return this;
    }
    liveUpdates(enabled) {
        if (enabled) {
            this.liveUpdates(false);
            const filter = typeof enabled === 'function' ? enabled : () => true;
            this._liveUpdates = {
                updated: json => {
                    filter(json) && this.add(json, { parse: true, merge: true });
                },
                removed: id => this.remove(id)
            };
            return this.getEndpoint().subscribe(this._liveUpdates, this).then(() => this);
        }
        else {
            if (this._liveUpdates) {
                this.getEndpoint().unsubscribe(this._liveUpdates, this);
                this._liveUpdates = null;
            }
        }
    }
    fetch(a_options = {}) {
        const options = Object.assign({ parse: true }, a_options), endpoint = this.getEndpoint();
        return startIO(this, endpoint.list(options, this), options, json => {
            let result = this.set(json, Object.assign({ parse: true, ioMethod: 'fetch' }, options));
            if (options.liveUpdates) {
                result = this.liveUpdates(options.liveUpdates);
            }
            return result;
        });
    }
    dispose() {
        if (this._disposed)
            return;
        const aggregated = !this._shared;
        for (let record of this.models) {
            free(this, record);
            if (aggregated)
                record.dispose();
        }
        this.liveUpdates(false);
        super.dispose();
    }
    reset(a_elements, options = {}) {
        const isRoot = begin(this), previousModels = this.models;
        if (a_elements) {
            emptySetTransaction(this, toElements(this, a_elements, options), options, true);
        }
        else {
            this._byId = {};
            this.models = [];
        }
        markAsDirty(this, options);
        options.silent || trigger2(this, 'reset', this, Object.assign({ previousModels }, options));
        const { _byId } = this;
        for (let toDispose of previousModels) {
            _byId[toDispose.cid] || free(this, toDispose);
        }
        isRoot && commit(this);
        return this.models;
    }
    add(a_elements, options = {}) {
        const elements = toElements(this, a_elements, options), transaction = this.models.length ?
            addTransaction(this, elements, options) :
            emptySetTransaction(this, elements, options);
        if (transaction) {
            transaction.commit();
            return transaction.added;
        }
    }
    remove(recordsOrIds, options = {}) {
        if (recordsOrIds) {
            return Array.isArray(recordsOrIds) ?
                removeMany(this, recordsOrIds, options) :
                removeOne(this, recordsOrIds, options);
        }
        return [];
    }
    _createTransaction(a_elements, options = {}) {
        const elements = toElements(this, a_elements, options);
        if (this.models.length) {
            return options.remove === false ?
                addTransaction(this, elements, options, true) :
                setTransaction(this, elements, options);
        }
        else {
            return emptySetTransaction(this, elements, options);
        }
    }
    sort(options = {}) {
        if (sortElements(this, options)) {
            const isRoot = begin(this);
            if (markAsDirty(this, options)) {
                trigger2(this, 'sort', this, options);
            }
            isRoot && commit(this);
        }
        return this;
    }
    unset(modelOrId, options) {
        const value = this.get(modelOrId);
        this.remove(modelOrId, Object.assign({ unset: true }, options));
        return value;
    }
    modelId(attrs) {
        return attrs[this.model.prototype.idAttribute];
    }
    toggle(model, a_next) {
        var prev = Boolean(this.get(model)), next = a_next === void 0 ? !prev : Boolean(a_next);
        if (prev !== next) {
            if (prev) {
                this.remove(model);
            }
            else {
                this.add(model);
            }
        }
        return next;
    }
    _log(level, topic, text, value, a_logger) {
        (a_logger || logger).trigger(level, topic, `${this.model.prototype.getClassName()}.${this.getClassName()}: ` + text, {
            Argument: value,
            'Attributes spec': this.model.prototype._attributes
        });
    }
    getClassName() {
        return super.getClassName() || 'Collection';
    }
    get length() { return this.models.length; }
    push(model, options) {
        return this.add(model, Object.assign({ at: this.length }, options));
    }
    pop(options) {
        var model = this.at(this.length - 1);
        this.remove(model, Object.assign({ unset: true }, options));
        return model;
    }
    unshift(model, options) {
        return this.add(model, Object.assign({ at: 0 }, options));
    }
    shift(options) {
        const model = this.at(0);
        this.remove(model, Object.assign({ unset: true }, options));
        return model;
    }
};
Collection._metatype = AggregatedType;
Collection = Collection_1 = __decorate([
    define({
        cidPrefix: 'c',
        model: Record,
        _changeEventName: 'changes',
        _aggregationError: null
    }),
    mixins(ArrayMixin),
    definitions({
        comparator: mixinRules.value,
        model: mixinRules.protoValue,
        itemEvents: mixinRules.merge
    })
], Collection);
export { Collection };
import { ArrayMixin } from './arrayMethods.js';
function toElements(collection, elements, options) {
    const parsed = options.parse ? collection.parse(elements, options) : elements;
    return Array.isArray(parsed) ? parsed : [parsed];
}
Record.Collection = Collection;
//# sourceMappingURL=index.js.map